<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import type { Game } from '@game/types'
import { computed, fallbackListSlug, toValue, useGameListTracking, useGames, useGamesJackpots, useI18n } from '#imports'

const props = withDefaults(defineProps<{
    pageSize?: number
    hasSidebar?: boolean
    lazy?: boolean
}>(), {
    pageSize: 25,
    hasSidebar: false,
    lazy: true,
})

const { t } = useI18n()
const { gamesJackpots } = useGamesJackpots()
const { trackingListSlug, trackingOrigin, onGameClick } = useGameListTracking('gametile', fallbackListSlug('JACKPOT'))

// Games are ordered by jackpot size, which only the jackpots feed knows.
const sortedJackpotSlugs = computed(() => {
    const jackpots = gamesJackpots.value
    if (isEmpty(jackpots)) return []

    return Object.keys(jackpots)
        .sort((a, b) => Number.parseFloat(String(jackpots[b]?.superJackpot ?? jackpots[b]?.jackpot)) - Number.parseFloat(String(jackpots[a]?.superJackpot ?? jackpots[a]?.jackpot)))
        .slice(0, props.pageSize)
})

const { data } = useGames({
    params: { page: 1, page_size: props.pageSize },
    filter: () => ({ slugs: sortedJackpotSlugs.value, has_jackpot: true }),
    lazy: props.lazy,
    key: () => `game-list-fallback:jackpot:${props.pageSize}`,
    enabled: () => !!toValue(sortedJackpotSlugs)?.length,
})

const games = computed(() => {
    const list = data.value?.data ?? []
    const slugIndex = new Map(sortedJackpotSlugs.value.map((slug, index) => [slug, index]))

    return list.slice().sort((a: Game, b: Game) =>
        (slugIndex.get(a.slug) ?? list.length) - (slugIndex.get(b.slug) ?? list.length),
    )
})
</script>

<template>
    <div v-if="games.length" class="custom-game-section">
        <GameList
            :games="games"
            :title="t('game.gameList.JACKPOT')"
            category="JACKPOT"
            :tracking-list-slug="trackingListSlug"
            :tracking-origin="trackingOrigin"
            :has-sidebar="hasSidebar"
            :video-enabled="true"
            :trim-dangling-tiles="true"
            @game-click="onGameClick"
        />
    </div>
</template>
